import type { ArtistMetrics } from '../../hooks/useArtistMetrics';

// --- Risk Score (1-10, higher = riskier) ---

export interface RiskAssessment {
    score: number; // 1-10
    factors: string[];
}

export function calculateRiskScore(
    metrics: ArtistMetrics | null,
    monthlyGrowthRate: number,
    historyCount: number
): RiskAssessment {
    if (!metrics) return { score: 10, factors: ['No data available'] };

    let score = 5; // baseline
    const factors: string[] = [];

    // Growth consistency
    if (monthlyGrowthRate < 0) {
        score += 2;
        factors.push('Declining listeners');
    } else if (monthlyGrowthRate < 0.02) {
        score += 1;
        factors.push('Slow growth');
    } else if (monthlyGrowthRate > 0.05) {
        score -= 1;
        factors.push('Strong growth trajectory');
    }

    // Career stage
    const stage = metrics.career_stage;
    if (stage === 'New') {
        score += 2;
        factors.push('Early-stage artist (higher variance)');
    } else if (stage === 'Emerging') {
        score += 1;
        factors.push('Emerging artist');
    } else if (stage === 'Mainstream') {
        score -= 2;
        factors.push('Mainstream artist (lower risk)');
    }

    // Platform diversity
    const platforms = [
        metrics.spotify_monthly_listeners,
        metrics.instagram_followers,
        metrics.tiktok_followers,
        metrics.youtube_subscribers,
    ].filter((v) => v != null && v > 1000);

    if (platforms.length >= 3) {
        score -= 1;
        factors.push(`Active on ${platforms.length} platforms`);
    } else if (platforms.length <= 1) {
        score += 1;
        factors.push('Limited platform presence');
    }

    // Data confidence
    if (historyCount < 3) {
        score += 1;
        factors.push('Limited historical data');
    }

    // Engagement signals
    const engRate = metrics.instagram_engagement_rate;
    if (engRate != null && engRate > 3) {
        score -= 1;
        factors.push('Strong engagement rate');
    } else if (engRate != null && engRate < 1) {
        score += 1;
        factors.push('Low engagement rate');
    }

    return {
        score: Math.max(1, Math.min(10, score)),
        factors,
    };
}

// --- Sync Score (1-5 qualitative tier) ---

export interface SyncScore {
    tier: number; // 1-5
    label: string;
    reasoning: string;
}

export function calculateSyncScore(
    metrics: ArtistMetrics | null
): SyncScore {
    if (!metrics) return { tier: 1, label: 'Unknown', reasoning: 'No data' };

    let tier = 1;
    const reasons: string[] = [];

    // Shazam is a strong sync indicator
    const shazam = metrics.shazam_count ?? 0;
    if (shazam > 100000) {
        tier += 2;
        reasons.push('High Shazam activity');
    } else if (shazam > 10000) {
        tier += 1;
        reasons.push('Moderate Shazam activity');
    }

    // Spotify playlist reach = discoverability
    const playlistReach = metrics.spotify_playlist_reach ?? 0;
    if (playlistReach > 1000000) {
        tier += 1;
        reasons.push('Strong playlist reach');
    }

    // Popularity score (Spotify's editorial signal)
    const popularity = metrics.spotify_popularity ?? 0;
    if (popularity > 50) {
        tier += 1;
        reasons.push('Good Spotify popularity');
    }

    tier = Math.max(1, Math.min(5, tier));

    const labels: Record<number, string> = {
        1: 'Low',
        2: 'Below Average',
        3: 'Average',
        4: 'Above Average',
        5: 'High',
    };

    return {
        tier,
        label: labels[tier],
        reasoning: reasons.length ? reasons.join('; ') : 'Limited sync indicators',
    };
}

// --- Brand Score (1-5 qualitative tier) ---

export interface BrandScore {
    tier: number; // 1-5
    label: string;
    reasoning: string;
}

export function calculateBrandScore(
    metrics: ArtistMetrics | null
): BrandScore {
    if (!metrics) return { tier: 1, label: 'Unknown', reasoning: 'No data' };

    let tier = 1;
    const reasons: string[] = [];

    // Instagram presence is the main brand signal
    const igFollowers = metrics.instagram_followers ?? 0;
    const igEngagement = metrics.instagram_engagement_rate ?? 0;

    if (igFollowers > 100000) {
        tier += 2;
        reasons.push('Large Instagram following');
    } else if (igFollowers > 20000) {
        tier += 1;
        reasons.push('Solid Instagram following');
    }

    if (igEngagement > 3) {
        tier += 1;
        reasons.push('High engagement rate');
    }

    // TikTok = modern brand relevance
    const tiktok = metrics.tiktok_followers ?? 0;
    if (tiktok > 50000) {
        tier += 1;
        reasons.push('Strong TikTok presence');
    }

    // YouTube = content capability
    const yt = metrics.youtube_subscribers ?? 0;
    if (yt > 50000) {
        tier += 1;
        reasons.push('Active YouTube channel');
    }

    tier = Math.max(1, Math.min(5, tier));

    const labels: Record<number, string> = {
        1: 'Low',
        2: 'Below Average',
        3: 'Average',
        4: 'Above Average',
        5: 'High',
    };

    return {
        tier,
        label: labels[tier],
        reasoning: reasons.length ? reasons.join('; ') : 'Limited brand indicators',
    };
}
